import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, ArrowRight, MessageSquare, Instagram, Linkedin, Twitter, CheckCircle2, Menu, X, Cpu, Globe, Layers, Sparkles } from "lucide-react";

export default function Contact() {
  const [scrollY, setScrollY] = React.useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formState, setFormState] = useState("idle"); // idle, sending, success
  const [selectedScope, setSelectedScope] = useState("Autonomous AI Infrastructure");
  const whatsappLink = "https://wa.me/923329945014";

  React.useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      name: formData.get('name'),
      email: formData.get('email'),
      scope: selectedScope,
      brief: formData.get('brief')
    };

    setFormState("sending");

    // Construct WhatsApp Message
    const message = `*New Strategic Inquiry from Velox*%0A%0A*Entity:* ${data.name}%0A*Channel:* ${data.email}%0A*Scope:* ${data.scope}%0A%0A*Strategic Brief:*%0A${data.brief}`;
    const fullWhatsappLink = `${whatsappLink}?text=${message}`;

    setTimeout(() => {
      setFormState("success");
      window.open(fullWhatsappLink, "_blank");
    }, 1500);
  };

  return (
    <div className="bg-white min-h-screen text-black font-inter selection:bg-[#FF6B00]/30 selection:text-white">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-[100] transition-all duration-500 ${scrollY > 50 ? 'backdrop-blur-xl bg-white/80 py-4 border-b border-black/5' : 'bg-transparent py-6 md:py-8'}`}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 flex justify-between items-center text-black">
          <Link to="/" className="text-xl sm:text-2xl md:text-3xl font-black tracking-tighter uppercase italic flex items-center gap-1 sm:gap-2">
             <span className="text-black">VELOX</span><span className="text-[#FF6B00]">.</span>
          </Link>
          
          <div className="hidden lg:flex items-center gap-10">
            <Link to="/ai-solutions" className="text-[14px] font-black uppercase tracking-[0.2em] text-gray-700 hover:text-[#FF6B00] transition-colors">Chatbot Solutions</Link>
            <Link to="/what-is-automation" className="text-[14px] font-black uppercase tracking-[0.2em] text-gray-700 hover:text-[#FF6B00] transition-colors">Chatbot Lab</Link>
            <Link to="/projects" className="text-[14px] font-black uppercase tracking-[0.2em] text-gray-700 hover:text-[#FF6B00] transition-colors">Web Artifacts</Link>
            <Link to="/pricing" className="text-[14px] font-black uppercase tracking-[0.2em] text-gray-700 hover:text-[#FF6B00] transition-colors">Pricing</Link>
            <Link to="/contact" className="px-10 py-5 bg-[#FF6B00] text-white font-black text-[14px] uppercase tracking-widest hover:bg-black transition-all rounded-sm shadow-xl shadow-[#FF6B00]/20">Reach Out</Link>
          </div>

          <button 
            className="lg:hidden text-black p-2 hover:bg-black/5 rounded-full transition-colors" 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle Menu"
          >
            {mobileMenuOpen ? <X size={24} className="sm:size-[28px]" /> : <Menu size={24} className="sm:size-[28px]" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div className={`fixed inset-0 z-[110] bg-black transition-all duration-700 flex flex-col items-center justify-center gap-10 ${mobileMenuOpen ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'}`}>
          <Link to="/ai-solutions" className="text-4xl font-black text-white hover:text-[#FF6B00]" onClick={() => setMobileMenuOpen(false)}>Chatbot Solutions</Link>
          <Link to="/what-is-automation" className="text-4xl font-black text-white hover:text-[#FF6B00]" onClick={() => setMobileMenuOpen(false)}>Chatbot Lab</Link>
          <Link to="/projects" className="text-4xl font-black text-white hover:text-[#FF6B00]" onClick={() => setMobileMenuOpen(false)}>Web Artifacts</Link>
          <Link to="/pricing" className="text-4xl font-black text-white hover:text-[#FF6B00]" onClick={() => setMobileMenuOpen(false)}>Pricing</Link>
          <button className="mt-10" onClick={() => setMobileMenuOpen(false)}>
            <X size={40} className="text-gray-500 hover:text-white" />
          </button>
      </div>

      <main className="pt-64 pb-32 px-6 md:px-12 relative overflow-hidden bg-white">
        <div className="max-w-[1400px] mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            {/* Left Side: Text & Info */}
            <div className="text-center lg:text-left flex flex-col items-center lg:items-start">
              <div className="text-[#FF6B00] font-black text-[10px] sm:text-[12px] uppercase tracking-[0.3em] sm:tracking-[0.6em] mb-6 sm:mb-8">Direct Access Terminal</div>
              <h1 className="text-4xl sm:text-6xl md:text-[120px] font-black tracking-tighter leading-[1] md:leading-[0.85] italic uppercase mb-6 sm:mb-12 text-black px-1 sm:px-0">
                 INITIATE<br/><span className="text-[#FF6B00]">COLLAB</span>
              </h1>
              <p className="text-base sm:text-2xl text-gray-600 max-w-xl font-medium leading-relaxed mb-10 sm:mb-16 italic uppercase tracking-wider">
                Our engineers are ready to deploy bespoke <span className="text-black font-bold">Neural Units</span> and <span className="text-black font-bold">Web Ecosystems</span>.
              </p>

              <div className="space-y-10 sm:space-y-12 w-full flex flex-col items-center lg:items-start">
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-8 text-center sm:text-left">
                  <div className="p-4 sm:p-5 bg-black/5 rounded-sm text-[#FF6B00]">
                    <Mail size={24} className="sm:size-[32px]" />
                  </div>
                  <div>
                    <div className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1 sm:mb-2">Electronic Mail</div>
                    <div className="text-base xs:text-lg sm:text-2xl font-black italic uppercase break-all sm:break-normal">velox.operations09@gmail.com</div>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-8 text-center sm:text-left">
                  <div className="p-4 sm:p-5 bg-black/5 rounded-sm text-[#FF6B00]">
                    <Phone size={24} className="sm:size-[32px]" />
                  </div>
                  <div>
                    <div className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-gray-500 mb-1 sm:mb-2">Neural Link (WhatsApp)</div>
                    <div className="text-xl sm:text-2xl font-black italic uppercase">+92 332 9945014</div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-8 text-center sm:text-left">
                  <div className="p-4 sm:p-5 bg-black/5 rounded-sm text-[#FF6B00]">
                    <MessageSquare size={24} className="sm:size-[32px]" />
                  </div>
                  <div>
                    <div className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest text-gray-600 mb-1 sm:mb-2">Instant Response</div>
                    <a href={whatsappLink} className="text-lg sm:text-xl font-black uppercase text-[#FF6B00] hover:underline decoration-2 underline-offset-8">Open Secure Chat</a>
                  </div>
                </div>
              </div>

              {/* Social Channels */}
              <div className="mt-16 md:mt-24 flex items-center justify-center lg:justify-start gap-8 md:gap-10">
                <Link to="#" className="text-gray-500 hover:text-white transition-colors"><Instagram size={20}/></Link>
                <Link to="#" className="text-gray-500 hover:text-white transition-colors"><Linkedin size={20}/></Link>
                <Link to="#" className="text-gray-500 hover:text-white transition-colors"><Twitter size={20}/></Link>
              </div>
            </div>

            {/* Right Side: Lead Gen Form */}
            <div className="p-10 md:p-16 bg-[#0a0a0a] text-white rounded-sm relative overflow-hidden group border border-white/5 shadow-2xl">
               <div className="absolute top-0 right-0 w-64 h-64 bg-[#FF6B00]/5 blur-[80px]" />
               
               {formState === "success" ? (
                 <div className="text-center py-20 animate-fadeInUp">
                    <div className="inline-flex items-center justify-center w-24 h-24 bg-[#FF6B00]/20 rounded-full mb-10">
                       <CheckCircle2 size={48} className="text-[#FF6B00]" />
                    </div>
                    <h2 className="text-4xl font-black italic uppercase mb-6">Transmission Received</h2>
                    <p className="text-gray-400 font-medium">A lead engineer will contact you within 6 business hours.</p>
                    <button 
                      onClick={() => setFormState("idle")}
                      className="mt-12 text-[#FF6B00] font-black uppercase tracking-widest hover:underline"
                    >
                      Send New Packet
                    </button>
                 </div>
               ) : (
                 <form onSubmit={handleSubmit} className="relative z-10 space-y-10">
                   <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
                      <div className="space-y-4">
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-500 ml-1">Entity Name</label>
                        <input name="name" required type="text" placeholder="e.g. Stark Industries" className="w-full bg-white/5 border border-white/10 p-4 md:p-5 font-bold focus:border-[#FF6B00] focus:bg-[#FF6B00]/5 transition-all outline-none" />
                      </div>
                      <div className="space-y-4">
                        <label className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-500 ml-1">Communication Channel</label>
                        <input name="email" required type="email" placeholder="email@address.com" className="w-full bg-white/5 border border-white/10 p-4 md:p-5 font-bold focus:border-[#FF6B00] focus:bg-[#FF6B00]/5 transition-all outline-none" />
                      </div>
                   </div>

                   <div className="space-y-6">
                      <label className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-500 ml-1">Project Scope</label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
                         {[
                           { id: "Autonomous AI Infrastructure", icon: <Cpu size={16} /> },
                           { id: "High-Performance Web Ecosystem", icon: <Globe size={16} /> },
                           { id: "Unified Digital Overhaul", icon: <Layers size={16} /> },
                           { id: "Specific Consultation", icon: <Sparkles size={16} /> }
                         ].map((scope) => (
                           <button
                             key={scope.id}
                             type="button"
                             onClick={() => setSelectedScope(scope.id)}
                             className={`flex items-center gap-3 md:gap-4 p-4 md:p-5 border-2 transition-all duration-300 rounded-sm text-left group ${selectedScope === scope.id ? 'bg-[#FF6B00]/10 border-[#FF6B00] shadow-[0_0_20px_rgba(255,107,0,0.2)]' : 'bg-white/5 border-white/10 hover:border-white/30'}`}
                           >
                             <div className={`p-2 rounded-full transition-colors ${selectedScope === scope.id ? 'bg-[#FF6B00] text-white' : 'bg-white/5 text-gray-500 group-hover:text-white'}`}>
                               {scope.icon}
                             </div>
                             <span className={`text-[9px] md:text-[11px] font-black uppercase tracking-widest ${selectedScope === scope.id ? 'text-white' : 'text-gray-400 group-hover:text-white'}`}>
                               {scope.id}
                             </span>
                           </button>
                         ))}
                      </div>
                   </div>

                   <div className="space-y-4">
                      <label className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-500 ml-1">Strategic Brief</label>
                      <textarea name="brief" required rows="4" placeholder="How can we help you dominate?" className="w-full bg-white/5 border border-white/10 p-5 font-bold focus:border-[#FF6B00] focus:bg-[#FF6B00]/5 transition-all outline-none resize-none" />
                   </div>

                   <button 
                    disabled={formState === "sending"}
                    className="w-full py-6 sm:py-8 bg-[#FF6B00] text-white font-black uppercase tracking-[0.2em] sm:tracking-[0.4em] hover:bg-black hover:text-white transition-all duration-700 disabled:opacity-50 flex items-center justify-center gap-4 text-base sm:text-lg rounded-sm"
                   >
                     {formState === "sending" ? "TRANSMITTING..." : "REACH OUT"}
                     <ArrowRight size={20} />
                   </button>
                 </form>
               )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer Minimal */}
      <footer className="py-12 md:py-20 px-4 md:px-6 bg-black border-t border-white/5 text-center">
         <div className="text-2xl md:text-3xl font-black text-white italic uppercase mb-6">VELOX<span className="text-[#FF6B00]">.</span></div>
         <p className="text-[8px] sm:text-[10px] font-medium sm:font-black uppercase tracking-[0.2em] sm:tracking-[0.4em] text-gray-700 max-w-[280px] md:max-w-none mx-auto">
            © 2026 VELOX UNIFIED SOLUTIONS UNIT. <br className="sm:hidden" /> ENGINEERING THE FUTURE. | <a href="mailto:velox.operations09@gmail.com" className="text-[#FF6B00]/80 lowercase tracking-normal sm:uppercase sm:tracking-widest">velox.operations09@gmail.com</a>
         </p>
      </footer>
    </div>
  );
}
